import * as Sentry from '@sentry/nextjs';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';

import type { TransactionModuleProps } from '@/components/TransactionModule/types';

import { CHAIN_IDS } from '@/constants/chain';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { CheckoutModes, useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { breakpoints } from '@/styles/util';

import { classNames } from '@/utils/classnames';
import { isTest } from '@/utils/environment';
import type { ChainAddress, EthereumAddress, SolanaAddress } from '@/utils/models';

import { AdvancedPanel } from './components/AdvancedPanel';
import { CrossChainPanel } from './components/CrossChainPanel';
import { MarketPanel } from './components/MarketPanel';
import { ModeTabs } from './components/ModeTabs';
import { ModuleErrorBoundary } from './components/ModuleErrorBoundary';
import { StatusScreen } from './components/status/StatusScreen';
import { ModuleProvider, type ModuleContextValue } from './ModuleContext';
import { useCheckout } from './useCheckout';
import { useSellAmountQuerySync } from './useSellAmountQuerySync';
import { useSyncInputUsd } from './useSyncInputUsd';
import { useTradeEngine } from './useTradeEngine';

const DeferredGlobalMarketSettingsModal = dynamic(
  () =>
    import('../Modal/GlobalMarketSettingsModal').then(
      ({ GlobalMarketSettingsModal }) => GlobalMarketSettingsModal,
    ),
  {},
);

/** Renders the transaction module from the page-owned trade engine. */
export function TransactionModule({
  autoBannerVisible,
  sellTokenChainId,
  sellTokenAddress,
  buyTokenChainId,
  buyTokenAddress,
  onFlipSelectedTokens,
  onSelectToken,
  onSelectPair,
  onOpenTokenPanel,
  setAutoBannerVisible,
  variant = 'fullpage',
  className,
}: TransactionModuleProps) {
  const { quote } = useTradeEngine();
  const [checkout, dispatchCheckout] = useCheckout();
  const [checkoutMode] = useCheckoutMode();
  const [advancedOrderType] = useAdvancedOrderType();
  const { token: sellToken, isLoading: isSellTokenLoading } = useTokenResult(
    sellTokenAddress,
    sellTokenChainId,
  );
  const { token: buyToken, isLoading: isBuyTokenLoading } = useTokenResult(
    buyTokenAddress,
    buyTokenChainId,
  );
  const { solanaWallet, ethereumWallet } = useMatchaWallets();
  const isMobile = useMedia(breakpoints.mobile, true);
  const [isSettingsOpen, setSettingsOpen] = useState(false);

  const accountAddress: ChainAddress | undefined =
    sellTokenChainId === CHAIN_IDS.SOLANA
      ? (solanaWallet?.address as SolanaAddress | undefined)
      : (ethereumWallet?.address as EthereumAddress | undefined);

  useSyncInputUsd({
    sellTokenAddress,
    sellTokenChainId,
    buyTokenAddress,
    buyTokenChainId,
  });
  useSellAmountQuerySync();

  useEffect(() => {
    Sentry.setTag('app.checkout_mode', checkoutMode);
    Sentry.setTag('app.sell_token.address', sellTokenAddress ?? 'not_set');
    Sentry.setTag('app.sell_token.chain_id', sellTokenChainId ?? 'not_set');
    Sentry.setTag('app.buy_token.address', buyTokenAddress ?? 'not_set');
    Sentry.setTag('app.buy_token.chain_id', buyTokenChainId ?? 'not_set');
    Sentry.setTag(
      'app.advanced_order_type',
      checkoutMode === CheckoutModes.advanced ? advancedOrderType : 'not_set',
    );
  }, [
    checkoutMode,
    advancedOrderType,
    sellTokenAddress,
    sellTokenChainId,
    buyTokenAddress,
    buyTokenChainId,
  ]);

  useEffect(() => {
    if (isMobile && !isTest) return;
    return () => dispatchCheckout({ type: 'reset' });
  }, [dispatchCheckout, isMobile]);

  const contextValue = useMemo<ModuleContextValue>(
    () => ({
      variant,
      sellToken,
      buyToken,
      isSellTokenLoading,
      isBuyTokenLoading,
      accountAddress,
      onSelectToken,
      onSelectPair,
      onFlipSelectedTokens,
      onOpenTokenPanel,
      autoBannerVisible,
      setAutoBannerVisible,
      setSettingsOpen,
    }),
    [
      accountAddress,
      autoBannerVisible,
      buyToken,
      isBuyTokenLoading,
      isSellTokenLoading,
      onFlipSelectedTokens,
      onOpenTokenPanel,
      onSelectPair,
      onSelectToken,
      sellToken,
      setAutoBannerVisible,
      variant,
    ],
  );
  const isWidgetVariant = variant === 'widget';
  const isMarketSource = quote.source === 'swap' || quote.source === 'intents';
  const showTransactionStatus = checkout.step === 'submitted' || checkout.step === 'submitFailed';
  const activePanel = isMarketSource ? (
    <MarketPanel />
  ) : quote.source === 'cross-chain' ? (
    <CrossChainPanel />
  ) : (
    <AdvancedPanel />
  );

  return (
    <div
      className={classNames(
        'flex flex-col justify-between overflow-hidden size-full',
        { 'min-h-[100dvh] tablet:min-h-auto rounded-none desktop:rounded-2xl': !isWidgetVariant },
        { 'rounded-2xl shadow-30': isWidgetVariant },
        className,
      )}
    >
      <ModuleProvider value={contextValue}>
        <ModuleErrorBoundary
          sellTokenChainId={sellTokenChainId}
          sellTokenAddress={sellTokenAddress}
          buyTokenChainId={buyTokenChainId}
          buyTokenAddress={buyTokenAddress}
        >
          <div
            className={classNames(
              'h-full overflow-y-auto overflow-x-hidden',
              'relative tablet:min-h-full tablet:overflow-y-auto',
              showTransactionStatus ? 'min-h-full' : 'min-h-[calc(100%-80px)]',
              { 'pb-20 tablet:pb-0': !isWidgetVariant && !showTransactionStatus },
            )}
          >
            {showTransactionStatus ? <StatusScreen /> : <ModeTabs>{activePanel}</ModeTabs>}
          </div>

          {(sellTokenChainId || buyTokenChainId) && (
            <DeferredGlobalMarketSettingsModal
              buyToken={buyToken}
              buyTokenChainId={buyTokenChainId}
              chainId={sellTokenChainId || buyTokenChainId!}
              sellToken={sellToken}
              shouldShow={isSettingsOpen}
              setShouldShow={setSettingsOpen}
            />
          )}
        </ModuleErrorBoundary>
      </ModuleProvider>
    </div>
  );
}
